import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from 'ui/shared/messages';
import { IconTextField } from 'ui/shared/controls';
import { TermsOfServiceDialog } from '../termsOfServiceDialog/TermsOfServiceDialog';

require('./PasswordPanel.css');

export type Props = {
    active: boolean;
    busy: boolean;
    errorMessage: string;
    password: string;
    setPassword(password: string): void;
};

export const PasswordPanel = ({ active, busy, errorMessage, password, setPassword }: Props) => {
    const { t } = useTranslation();

    const [ showTos, setTosVisible ] = useState(false);

    const passwordInputRef = useRef<HTMLInputElement>();
    const timerRef = useRef<NodeJS.Timeout>();

    // HACK: Set focus a short time after this panel becomes active due to an error message
    useEffect(() => {
        if (active && !password && errorMessage) {
            timerRef.current = setTimeout(() => {
                passwordInputRef.current && passwordInputRef.current.focus();
            }, 400);
            return () => clearTimeout(timerRef.current!);
        }
    }, [ passwordInputRef, timerRef, active, password, errorMessage ]);

    return (
        <>
            <div
                id="password-panel"
                className={ `panel ${active ? 'active' : ''}` }
            >
                <div>
                    <IconTextField
                        id="login-password"
                        setRef={ passwordInputRef }
                        labelKey="LOGIN.FIELD.PASSWORD"
                        icon="lock"
                        type="password"
                        required={ true }
                        maxLength={ 64 }
                        tabIndex={ active ? 1 : -1 }
                        disabled={ busy || !active }
                        value={ password }
                        onChange={ e => setPassword(e.currentTarget.value) }
                    />
                </div>
                <div style={ { marginTop: '1em', textAlign: 'right' } }>
                    <Link
                        id="reset-password-link"
                        to="/reset-password"
                        tabIndex={ active ? 1 : -1 }
                    >
                        { t('LOGIN.FORGOT_PASSWORD') }
                    </Link>
                    <button
                        id="login-button"
                        className="ui primary submit button"
                        type={ active ? 'submit' : 'button' }
                        tabIndex={ active ? 1 : -1 }
                        disabled={ busy || !active || !password }
                    >
                        { t('COMMAND.LOGIN') }
                    </button>
                </div>

                <ErrorMessage message={ errorMessage }/>
            </div>

            <TermsOfServiceDialog
                visible={ showTos }
                onClickClose={ () => setTosVisible(false) }
            />
        </>
    );
};
